import { plusBusinessDays } from '@/helpers/dates';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { Model } from 'pinia-orm';
import { BelongsTo, MorphMany, Num, Str } from 'pinia-orm/dist/decorators';
import Comment from './Comment';
import Delay from './Delay';
import Project from './Project';
import User from './User';
import VariationOrder from './VariationOrder';

export const ContractEventStatus = {
  pending: 0,
  registered_as_variation_order: 10,
  registered_as_delay: 20,
  registered_as_variation_order_and_delay: 25,
  declined: 30,
} as const;

export const ContractEventStatusTitles = {
  pending: 'Pending',
  registered_as_variation_order: 'Registered as variation order',
  registered_as_delay: 'Registered as delay',
  registered_as_variation_order_and_delay: 'Registered as variation order and delay',
  declined: 'Declined',
};

// pending: 'bg-amber-50 border-amber-200 text-amber-800',
// registered_as_variation_order: 'bg-purple-50 border-purple-200 text-purple-800',
// registered_as_delay: 'bg-blue-50 border-blue-200 text-blue-800',

export const ContractEventStatusBadgeClasses = {
  pending: 'bg-amber-100 border-amber-200 text-amber-800',
  registered_as_variation_order: 'bg-purple-100 border-purple-200 text-purple-800',
  registered_as_delay: 'bg-blue-100 border-blue-200 text-blue-800',
  registered_as_variation_order_and_delay: 'bg-purple-100 border-purple-200 text-purple-800',
  declined: 'bg-gray-100 border-gray-200 text-gray-800',
} as const;

export const ContractEventImpact = {
  low: 0,
  medium: 10,
  high: 20,
} as const;

export const ContractEventImpactBadgeClasses = {
  low: 'bg-yellow-100 border-yellow-200 text-yellow-800',
  medium: 'bg-orange-100 border-orange-200 text-orange-800',
  high: 'bg-red-100 border-red-200 text-red-800',
} as const;

export const ContractEventImpactTitles = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export default class ContractEvent extends Model {
  static entity = 'App\\Models\\ContractEvent';

  @Num(0)
  declare id: number;
  @Num(0)
  declare project_id: number;
  @Num(0)
  declare user_id: number;
  @Str('')
  declare code: string;
  @Str('')
  declare name: string;
  @Str('')
  declare cause: string;
  @Str('')
  declare effect: string;
  @Num(10)
  declare impact: ObjectValues<typeof ContractEventImpact>;
  @Num(undefined)
  declare status: ObjectValues<typeof ContractEventStatus>;
  @Str('')
  declare date_of_occurrence: string | undefined;
  @Num(undefined)
  declare variation_order_id: number | undefined;
  @Num(undefined)
  declare delay_id: number | undefined;
  @Str('')
  declare created_at: string;
  @Str('')
  declare updated_at: string;
  @Str('')
  declare next_action: string | undefined;
  @Str('')
  declare next_action_due_date: string | undefined;
  @Num(undefined)
  declare next_action_assignee_id: number | undefined;
  @BelongsTo(() => Project, 'project_id', 'id')
  declare project: Project;
  @BelongsTo(() => User, 'user_id', 'id')
  declare user: User;
  @BelongsTo(() => User, 'next_action_assignee_id', 'id')
  declare next_action_assignee: User | null;
  @BelongsTo(() => VariationOrder, 'variation_order_id', 'id')
  declare variation_order: VariationOrder | null;
  @BelongsTo(() => Delay, 'delay_id', 'id')
  declare delay: Delay | null;
  @MorphMany(() => Comment, 'commentable_id', 'commentable_type', 'id')
  declare comments: Comment[];

  get createdAt(): DateTime {
    return DateTime.fromISO(this.created_at);
  }

  get dateOfOccurrence(): DateTime | undefined {
    return this.date_of_occurrence ? DateTime.fromISO(this.date_of_occurrence) : undefined;
  }

  get nextActionDueIn(): number | undefined {
    if(!this.next_action_due_date) {
      return undefined;
    }

    return Math.ceil(DateTime.fromISO(this.next_action_due_date).diffNow('days').days);
  }

  get raiseDueIn(): number | undefined {
    const projectsStore = useProjectsStore();
    const project = projectsStore.models.find(this.project_id);

    if(!this.dateOfOccurrence || !project?.head_contract_settings.cen_raise_required) {
      return undefined;
    }

    const settings = project.head_contract_settings;

    const dueDate = settings.cen_raise_business_days ?
      plusBusinessDays(this.dateOfOccurrence, settings.holidays, settings.cen_raise_period) :
      this.dateOfOccurrence.plus({ days: settings.cen_raise_period });

    return Math.ceil(dueDate.diffNow('days').days);
  }
}
